/**
 * API service for backend communication
 */
import axios from 'axios';
import type { AxiosInstance, AxiosError } from 'axios';
import { API_CONFIG } from '../config/api';
import type {
  Voucher,
  VoucherCreate,
  VoucherUpdate,
  VoucherListResponse,
  VoucherFilter,
  VoucherStatistics,
  GmailConnection,
  GmailConnectionUpdate,
  GmailLabel,
} from '../types';

class ApiService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_CONFIG.baseURL,
      timeout: API_CONFIG.timeout,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor - rely on session cookie; no auth header needed
    this.client.interceptors.request.use(
      (config) => {
        // Ensure credentials are included for cookie-based session
        config.withCredentials = true;
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Voucher endpoints
  async createVoucher(data: VoucherCreate): Promise<Voucher> {
    const response = await this.client.post<Voucher>('/api/vouchers', data);
    return response.data;
  }

  async getVouchers(
    filters?: VoucherFilter,
    page = 1,
    pageSize = 50
  ): Promise<VoucherListResponse> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });
    }
    params.append('page', String(page));
    params.append('page_size', String(pageSize));

    const response = await this.client.get<VoucherListResponse>(
      `/api/vouchers?${params.toString()}`
    );
    return response.data;
  }

  async getVoucher(id: string): Promise<Voucher> {
    const response = await this.client.get<Voucher>(`/api/vouchers/${id}`);
    return response.data;
  }

  async updateVoucher(id: string, data: VoucherUpdate): Promise<Voucher> {
    const response = await this.client.patch<Voucher>(
      `/api/vouchers/${id}`,
      data
    );
    return response.data;
  }

  async markVoucherUsed(id: string, usedDate?: string): Promise<Voucher> {
    const response = await this.client.post<Voucher>(
      `/api/vouchers/${id}/mark-used`,
      usedDate ? { used_date: usedDate } : {}
    );
    return response.data;
  }

  async deleteVoucher(id: string): Promise<void> {
    await this.client.delete(`/api/vouchers/${id}`);
  }

  async uploadEmlFile(file: File): Promise<Voucher[]> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await this.client.post<Voucher[]>(
      '/api/vouchers/upload',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  }

  async getStatistics(): Promise<VoucherStatistics> {
    const response = await this.client.get<VoucherStatistics>(
      '/api/vouchers/statistics'
    );
    return response.data;
  }

  async bulkDeleteVouchers(voucherIds: string[]): Promise<{ deleted_count: number; total_requested: number }> {
    const response = await this.client.post<{ deleted_count: number; total_requested: number }>(
      '/api/vouchers/bulk-delete',
      voucherIds
    );
    return response.data;
  }

  async bulkMarkUsed(voucherIds: string[], usedDate?: string): Promise<{ updated_count: number; total_requested: number }> {
    const url = usedDate 
      ? `/api/vouchers/bulk-mark-used?used_date=${usedDate}`
      : '/api/vouchers/bulk-mark-used';
    const response = await this.client.post<{ updated_count: number; total_requested: number }>(
      url,
      voucherIds
    );
    return response.data;
  }

  async getBrands(): Promise<string[]> {
    const response = await this.client.get<string[]>('/api/vouchers/brands');
    return response.data;
  }

  // Health check
  async healthCheck(): Promise<{ status: string; version: string }> {
    const response = await this.client.get('/health');
    return response.data;
  }

  // Logout - clears session cookie
  async logout(): Promise<void> {
    await fetch(`${this.client.defaults.baseURL}/api/auth/logout`, {
      method: 'POST',
      credentials: 'include', // Include cookies
    });
  }

  // OAuth callback is now handled by backend GET endpoint
  // Backend redirects to frontend with cookie set

  // Get current user (session is cookie-based)
  async getMe(): Promise<{
    user_id: string;
    email?: string;
    organization_id?: string;
  } | null> {
    const response = await fetch(`${this.client.defaults.baseURL}/api/auth/me`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Include cookies
    });

    if (response.status === 401) {
      return null;
    }

    if (!response.ok) {
      throw new Error('Failed to get user info');
    }

    return response.json();
  }

  // Gmail Connection endpoints
  async createGmailConnection(): Promise<GmailConnection> {
    const response = await this.client.post<GmailConnection>('/api/gmail/connections', {});
    return response.data;
  }

  async getGmailConnections(): Promise<GmailConnection[]> {
    const response = await this.client.get<{ connections: GmailConnection[] }>(
      '/api/gmail/connections'
    );
    return response.data.connections;
  }

  async getGmailConnection(connectionId: string): Promise<GmailConnection> {
    const response = await this.client.get<GmailConnection>(
      `/api/gmail/connections/${connectionId}`
    );
    return response.data;
  }

  async updateGmailConnection(
    connectionId: string,
    data: GmailConnectionUpdate
  ): Promise<GmailConnection> {
    const response = await this.client.patch<GmailConnection>(
      `/api/gmail/connections/${connectionId}`,
      data
    );
    return response.data;
  }

  async deleteGmailConnection(connectionId: string): Promise<void> {
    await this.client.delete(`/api/gmail/connections/${connectionId}`);
  }

  async getGmailLabels(connectionId: string): Promise<GmailLabel[]> {
    const response = await this.client.get<{ labels: GmailLabel[] }>(
      `/api/gmail/connections/${connectionId}/labels`
    );
    return response.data.labels;
  }
}

export const apiService = new ApiService();

